/**
 * KPI recipes and history (decision 0097). Pure: which items name a KPI and which recurring items feed its recipe, the
 * samples as logged (with the replaced ones), the work around a KPI from the event log ("markers", goalposts P3), and a
 * compact digest for agents. Markers are placed by time and linked by the plan (the goals that use the KPI, the bets on
 * it, the items that name it); they never claim a cause.
 */
import { KPI_MARKERS_MAX } from "@minga/protocol"
import { kpiPeriodLabel, kpiValueText } from "./kpi-period"
import { goalOf, itemOf } from "./state"
import { ancestorsOf, topItemOf } from "./tree"
import type {
  Actor,
  Goal,
  Item,
  Kpi,
  KpiEvidence,
  KpiHistorySample,
  KpiMarker,
  KpiSample,
  ProjectEvent,
  ProjectState,
} from "./types"

const refOrKey = (k: Pick<Kpi, "ref" | "key">): RegExp =>
  new RegExp(`(^|[^A-Za-z0-9-])(${k.ref}|${k.key})(?![A-Za-z0-9-])`, "i")

/** Does this item itself name the KPI: a goal that uses it, a bet on it, or its ref (K-3) or key in the title or description? */
const namesDirectly = (s: ProjectState, i: Item, k: Kpi): boolean =>
  (i.bet !== null && (i.bet.inputKpiId === k.id || i.bet.laggingKpiId === k.id)) ||
  i.goalIds.some((g) => {
    const goal = goalOf(s, g)
    return goal !== undefined && (goal.target?.kpiId === k.id || goal.kpiIds.includes(k.id))
  }) ||
  refOrKey(k).test(`${i.title}\n${i.body}`)

/**
 * Does the item, or an item above it, name the KPI (decision 0045)? A worker records only the KPIs its item names; an
 * occurrence of a recurring item names what its series names.
 */
export const itemNamesKpi = (s: ProjectState, item: Item, k: Kpi): boolean =>
  [item, ...ancestorsOf(s, item)].some((i) => namesDirectly(s, i, k))

/** The KPIs an item names (directly or above it): the recipes a worker on it follows. */
export const kpisNamedBy = (s: ProjectState, item: Item): ReadonlyArray<Kpi> =>
  s.kpis.filter((k) => itemNamesKpi(s, item, k))

/** The goals that use the KPI (as their target or a supporting KPI). */
export const kpiGoals = (s: ProjectState, k: Pick<Kpi, "id">): ReadonlyArray<Goal> =>
  s.goals.filter((g) => g.target?.kpiId === k.id || g.kpiIds.includes(k.id))

const isOpen = (t: Item): boolean => t.status !== "done" && t.status !== "cancelled"

/**
 * Decision 0097: the open recurring items that name the KPI. Each occurrence is ordinary worker work that follows the
 * KPI's recipe (the clock wakes the series' owner agent when it is due; it presses Run now).
 */
export const kpiFeeders = (s: ProjectState, k: Kpi): ReadonlyArray<Item> =>
  s.items.filter((t) => t.recurrence !== null && isOpen(t) && namesDirectly(s, t, k))

// #region samples
/** One logged KpiSampleRecorded with its envelope's facts. */
export interface LoggedKpiSample {
  readonly seq: number
  readonly ts: number
  readonly actor: Actor
  readonly sample: KpiSample
  readonly evidence?: ReadonlyArray<KpiEvidence> | undefined
}

/** The samples as logged, oldest period first; within a period the newest record wins, the others are `replaced`. */
export const kpiHistorySamples = (rows: ReadonlyArray<LoggedKpiSample>): ReadonlyArray<KpiHistorySample> => {
  const sorted = [...rows].sort((a, b) => a.seq - b.seq)
  const winner = new Map<number, number>()
  for (const r of sorted) winner.set(r.sample.at, r.seq)
  return sorted
    .map((r) => ({
      ...r.sample,
      recordedAt: r.ts as KpiHistorySample["recordedAt"],
      by: r.actor,
      evidence: [...(r.evidence ?? [])],
      replaced: winner.get(r.sample.at) !== r.seq,
    }))
    .sort((a, b) => a.at - b.at || a.recordedAt - b.recordedAt)
}

/** The samples that count (one per period), oldest first. */
export const effectiveSamples = (samples: ReadonlyArray<KpiHistorySample>): ReadonlyArray<KpiHistorySample> =>
  samples.filter((x) => !x.replaced)
// #endregion samples

// #region markers
/** The event tags a marker can come from (the store reads only these). */
export const KPI_MARKER_TAGS: ReadonlyArray<ProjectEvent["_tag"]> = [
  "ItemCreated",
  "ItemStatusChanged",
  "MergeFinished",
  "BetVerdictRecorded",
  "CommentPosted",
  "TargetChanged",
  "GoalUpdated",
  "GoalStatusChanged",
  "KpiDefined",
  "KpiUpdated",
]

export interface LoggedEvent {
  readonly seq: number
  readonly ts: number
  readonly actor: Actor
  readonly event: ProjectEvent
}

const oneLine = (text: string, max = 120): string => {
  const t = text.replace(/\s+/g, " ").trim()
  return t.length > max ? `${t.slice(0, max - 1)}…` : t
}

const changedKeys = (patch: object): string =>
  Object.entries(patch)
    .filter(([, v]) => v !== undefined)
    .map(([key]) => key)
    .join(", ")

/**
 * What happened around a KPI, oldest first (at most {@link KPI_MARKERS_MAX}, the newest kept): top-level work planned,
 * started or finished for a goal that uses it, a bet on it or an item that names it; merges under that work; bet
 * verdicts; decision comments on those goals and items; target, goal and KPI changes; and every finished release.
 * Linked by today's plan (an item moved since counts where it is now).
 */
export const kpiMarkers = (
  s: ProjectState,
  k: Kpi,
  events: ReadonlyArray<LoggedEvent>,
): ReadonlyArray<KpiMarker> => {
  const goalIds = new Set<string>(kpiGoals(s, k).map((g) => g.id))
  const linkedTop = new Map<string, boolean>()
  const isLinkedTop = (t: Item): boolean => {
    let v = linkedTop.get(t.id)
    if (v === undefined) {
      v = namesDirectly(s, t, k) || t.goalIds.some((g) => goalIds.has(g))
      linkedTop.set(t.id, v)
    }
    return v
  }
  const linked = (itemId: string | null): Item | undefined => {
    const t = itemId ? itemOf(s, itemId as Item["id"]) : undefined
    if (!t) return undefined
    const top = topItemOf(s, t)
    return isLinkedTop(top) ? t : undefined
  }
  const out: KpiMarker[] = []
  const push = (
    l: LoggedEvent,
    kind: KpiMarker["kind"],
    ref: string | null,
    title: string,
    note: string | null = null,
  ) =>
    out.push({
      at: l.ts as KpiMarker["at"],
      seq: l.seq,
      kind,
      ref,
      title: oneLine(title),
      note: note === null ? null : oneLine(note, 200),
      by: l.actor,
    })
  const goalRef = (id: string) => goalOf(s, id as Goal["id"])
  for (const l of [...events].sort((a, b) => a.seq - b.seq)) {
    const e = l.event
    switch (e._tag) {
      case "ItemCreated": {
        const t = itemOf(s, e.item.id) ?? e.item
        if (t.parentId === null && isLinkedTop(t)) push(l, "planned", t.ref, t.title, t.bet ? "a bet" : null)
        break
      }
      case "ItemStatusChanged": {
        const t = itemOf(s, e.itemId)
        if (!t || t.parentId !== null) break
        const release = t.type === "release"
        if (e.to === "done" && release) push(l, "release", t.ref, t.title)
        else if (!isLinkedTop(t)) break
        else if (e.to === "done") push(l, "done", t.ref, t.title)
        else if (e.to === "in_progress" && e.from !== "in_progress")
          push(l, "started", t.ref, t.title, t.bet ? "a bet" : null)
        break
      }
      case "MergeFinished": {
        if (e.result._tag !== "merged") break
        const t = linked(e.itemId)
        if (t) push(l, "merged", t.ref, t.title)
        break
      }
      case "BetVerdictRecorded": {
        const t = itemOf(s, e.itemId)
        if (t?.bet && (t.bet.inputKpiId === k.id || t.bet.laggingKpiId === k.id))
          push(l, "verdict", t.ref, t.title, e.value.replace(/_/g, " "))
        break
      }
      case "CommentPosted": {
        const c = e.comment
        if (c.kind !== "decision") break
        const note = c.tainted ? null : c.body
        if (c.target._tag === "goal" && goalIds.has(c.target.id)) {
          const g = goalRef(c.target.id)
          push(l, "decision", g?.ref ?? null, g?.title ?? "a goal", note)
        } else if (c.target._tag === "item") {
          const t = linked(c.target.id)
          if (t) push(l, "decision", t.ref, t.title, note)
        }
        break
      }
      case "TargetChanged": {
        if (e.subject._tag !== "goal" || !goalIds.has(e.subject.id)) break
        const g = goalRef(e.subject.id)
        const was = e.before ? `${e.before.op} ${e.before.value}` : "none"
        push(
          l,
          "target",
          g?.ref ?? null,
          g?.title ?? "a goal",
          `target ${e.condition.op} ${e.condition.value} (was ${was})`,
        )
        break
      }
      case "GoalUpdated": {
        if (!goalIds.has(e.goalId)) break
        const g = goalRef(e.goalId)
        push(l, "target", g?.ref ?? null, g?.title ?? "a goal", `changed ${changedKeys(e.patch)}`)
        break
      }
      case "GoalStatusChanged": {
        if (!goalIds.has(e.goalId)) break
        const g = goalRef(e.goalId)
        push(l, "goal", g?.ref ?? null, g?.title ?? "a goal", `marked ${e.status}`)
        break
      }
      case "KpiDefined":
        if (e.kpi.id === k.id) push(l, "kpi", k.ref, k.name, "defined")
        break
      case "KpiUpdated":
        if (e.kpiId === k.id) push(l, "kpi", k.ref, k.name, `changed ${changedKeys(e.patch)}`)
        break
      default:
        break
    }
  }
  return out.slice(-KPI_MARKERS_MAX)
}
// #endregion markers

// #region digest
const isoDay = (at: number): string => new Date(at).toISOString().slice(0, 10)

/** "+3 (+12%)", "−2", "±0". */
export const kpiDeltaText = (k: Pick<Kpi, "unit">, from: number, to: number): string => {
  const d = to - from
  const abs = Math.round(Math.abs(d) * 1000) / 1000
  if (d === 0) return "±0"
  const sign = d > 0 ? "+" : "−"
  const pct = from !== 0 ? ` (${sign}${Math.round((Math.abs(d) / Math.abs(from)) * 1000) / 10}%)` : ""
  return `${sign}${k.unit === "%" ? `${abs} pts` : abs}${pct}`
}

/** Better or worse for this KPI, given which way is good. */
export const kpiMoveWord = (k: Pick<Kpi, "direction">, from: number, to: number): string =>
  to === from ? "flat" : to > from === (k.direction === "up") ? "better" : "worse"

const conditionMet = (op: string, value: number, target: number): boolean => {
  switch (op) {
    case ">=":
      return value >= target
    case ">":
      return value > target
    case "<=":
      return value <= target
    case "<":
      return value < target
    default:
      return value === target
  }
}

/** How the number was measured, in a few words (the first evidence). */
export const evidenceText = (e: ReadonlyArray<KpiEvidence>): string | null => {
  const x = e[0]
  if (!x) return null
  switch (x._tag) {
    case "query":
      return `${x.source}: ${oneLine(x.query, 80)}`
    case "tool_call":
      return `tool ${x.tool}`
    case "url":
      return x.url
    case "note":
      return oneLine(x.text, 80)
    case "run":
      return `run ${x.runId}`
    case "artifact":
      return `output ${x.artifactId}`
  }
}

/**
 * Decision 0097: a KPI's history in a few lines for an agent judging it: the latest number and its change against the
 * previous one and each goal's target, the recent samples, the feeding recurring items and what happened around it.
 */
export const kpiDigest = (
  s: ProjectState,
  k: Kpi,
  history: { readonly samples: ReadonlyArray<KpiHistorySample>; readonly markers: ReadonlyArray<KpiMarker> },
  opts: {
    readonly name: (a: Actor) => string
    readonly recent?: number
    readonly around?: number
  },
) => {
  const eff = effectiveSamples(history.samples)
  const last = eff.at(-1)
  const prev = eff.at(-2)
  const first = eff[0]
  const period = (at: number) => kpiPeriodLabel(k.cadence, at)
  const sampleLine = (x: KpiHistorySample) => ({
    period: period(x.at),
    value: x.value,
    ...(x.provisional ? { provisional: true } : {}),
    by: opts.name(x.by),
    ...(evidenceText(x.evidence) ? { how: evidenceText(x.evidence) } : {}),
  })
  return {
    latest: last
      ? {
          ...sampleLine(last),
          ...(prev
            ? {
                vsPrevious: `${kpiDeltaText(k, prev.value, last.value)} vs ${period(prev.at)} (${kpiMoveWord(k, prev.value, last.value)})`,
              }
            : {}),
        }
      : null,
    trend:
      first && last && first !== last
        ? `${kpiValueText(k, first.value)} → ${kpiValueText(k, last.value)} over ${eff.length} periods since ${isoDay(first.at)}: ${kpiDeltaText(k, first.value, last.value)}, ${kpiMoveWord(k, first.value, last.value)}`
        : null,
    targets: kpiGoals(s, k)
      .filter((g) => g.target?.kpiId === k.id)
      .map((g) => {
        const t = g.target as NonNullable<Goal["target"]>
        return {
          goal: g.ref,
          target: `${t.op} ${t.value}`,
          ...(last
            ? {
                met: conditionMet(t.op, last.value, t.value),
                gap: kpiDeltaText(k, last.value, t.value),
              }
            : {}),
          ...(g.deadline !== null ? { deadline: isoDay(g.deadline) } : {}),
        }
      }),
    recent: eff.slice(-(opts.recent ?? 8)).map(sampleLine),
    around: history.markers
      .slice(-(opts.around ?? 12))
      .reverse()
      .map((m) => ({
        date: isoDay(m.at),
        what: m.kind,
        ...(m.ref ? { ref: m.ref } : {}),
        title: m.title,
        ...(m.note ? { note: m.note } : {}),
      })),
  }
}
// #endregion digest
